import { Modal } from 'is24-corecss';
import React, { useCallback, useEffect } from 'react';
import { css } from '@emotion/css';
import { IconS24InfoCircle24, IconS24ShieldCheckmark24 } from '@is24/cosma-ui-icons';

import UnitedSVG from './UnitedSVG';
import { VOUCHER_UPSELL_PARAM } from './constants';
import { useShareVoucher } from './useShareVoucher.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useQueryParam } from '@/customHooks/useQueryParam.hook';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useGenerateVoucher } from '@/apiHooks/voucher.hook';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { campaigns } from '@/components/common/campaigns';

const styles = {
  code: css`
    display: inline-block;
    min-width: 180px;

    &.loading {
      background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-300) 50%, var(--gray-200) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      color: transparent;
      user-select: none;
      border-radius: var(--border-radius-200);
      padding: var(--spacing-200) var(--spacing-300);
    }

    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `,
  voucherSection: css`
    margin-left: var(--size-700);
    margin-bottom: var(--spacing-800);

    @media (max-width: 768px) {
      margin-bottom: var(--spacing-600);
    }
  `,
  copyButtonWrapper: css`
    isolation: isolate;
    z-index: 10;
  `,
  notificationBox: css`
    z-index: 1;
  `,
  copyButton: css`
    width: var(--spacing-800);
    height: var(--spacing-800);
    border-radius: var(--border-radius-rounded);
    background-color: var(--gray-200);
    overflow: hidden;
    transition: background-color 0.2s ease-out;

    @media (max-width: 768px) {
      width: var(--size-1000);
      height: var(--size-1000);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover:not(:active) {
        background-color: var(--gray-300);
      }
    }

    &:active {
      background-color: var(--gray-400);
    }

    &:focus-visible {
      outline: 2px solid var(--system-blue600);
      outline-offset: 2px;
    }
  `,
  iconWrapper: css`
    width: var(--size-350);
    height: var(--size-350);
    pointer-events: none;
  `,
  icon: css`
    top: 50%;
    left: 50%;
    transition:
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  `,
  iconVisible: css`
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) rotate(0deg);
  `,
  iconHidden: css`
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.7) rotate(-45deg);
  `,
  copyButtonCopied: css`
    background-color: var(--system-green100) !important;

    @keyframes successRipple {
      0% {
        box-shadow: 0 0 0 0 rgb(82 196 26 / 40%);
      }
      50% {
        box-shadow: 0 0 0 8px rgb(82 196 26 / 0%);
      }
      100% {
        box-shadow: 0 0 0 0 rgb(82 196 26 / 0%);
      }
    }

    animation: successRipple 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  `,
  copiedBadge: css`
    top: calc(100% + var(--spacing-200));
    left: 50%;
    transform: translateX(-50%);
    background-color: rgb(0 0 0 / 75%);
    color: var(--gray-100);
    padding: var(--spacing-100) var(--spacing-300);
    border-radius: var(--border-radius-200);
    box-shadow: 0 2px 6px rgb(0 0 0 / 10%);
    animation: fadeInDown 0.2s ease-out;
    pointer-events: none;
    z-index: 10;
    will-change: opacity, transform;

    @keyframes fadeInDown {
      0% {
        opacity: 0;
        transform: translateX(-50%) translateY(-4px);
      }
      100% {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  `,
};

const CopyIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M13.3333 0H4V4H0V13.3333H9.33333V9.33333H13.3333V0ZM8 12H1.33333V5.33333H8V12ZM12 8H9.33333V4H5.33333V1.33333H12V8Z'
      fill='var(--gray-1000)'
    />
  </svg>
);

const CheckIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M5.6 10.6L2 7L3.4 5.6L5.6 7.8L10.6 2.8L12 4.2L5.6 10.6Z' fill='var(--system-green600)' />
  </svg>
);

const ShareIcon = () => (
  <svg width='13' height='14' viewBox='0 0 13 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M9.93721 8.03169C9.57016 8.03357 9.2075 8.1117 8.87222 8.26115C8.53694 8.41059 8.23636 8.62808 7.98952 8.89984L5.22035 7.29609C5.2689 7.1031 5.29556 6.90525 5.29985 6.70628C5.30081 6.48294 5.27411 6.26035 5.22035 6.04358L7.98952 4.46634C8.23219 4.74655 8.53091 4.97272 8.8664 5.13025C9.20189 5.28777 9.56668 5.37315 9.93721 5.38087C10.4988 5.38918 11.0488 5.22145 11.5102 4.9012C11.9717 4.58095 12.3213 4.12424 12.5101 3.59515C12.6989 3.06605 12.7174 2.49111 12.563 1.95095C12.4087 1.4108 12.0891 0.932538 11.6493 0.583248C11.2094 0.233959 10.6713 0.0311643 10.1104 0.0033102C9.54944 -0.0245439 8.99387 0.123939 8.52159 0.427933C8.04931 0.731927 7.68399 1.17618 7.47689 1.69838C7.26979 2.22059 7.23129 2.79454 7.36679 3.33974L4.59762 4.90372C4.23578 4.51137 3.76395 4.23744 3.2439 4.11778C2.72384 3.99813 2.17979 4.03833 1.68295 4.23312C1.18611 4.42791 0.759643 4.76822 0.459359 5.2095C0.159076 5.65078 -0.00102758 6.17247 4.96298e-06 6.70628C4.96298e-06 7.40932 0.279192 8.08357 0.776149 8.58069C1.27311 9.07782 1.94712 9.3571 2.64993 9.3571C3.01698 9.35523 3.37964 9.27709 3.71491 9.12765C4.05019 8.9782 4.35078 8.76071 4.59762 8.48896L7.36679 10.0198C7.31303 10.2366 7.28632 10.4592 7.28729 10.6825C7.28729 11.2068 7.4427 11.7193 7.73388 12.1552C8.02506 12.5912 8.43892 12.9309 8.92313 13.1316C9.40734 13.3322 9.94015 13.3847 10.4542 13.2824C10.9682 13.1801 11.4404 12.9276 11.811 12.5569C12.1816 12.1862 12.434 11.7139 12.5362 11.1997C12.6385 10.6855 12.586 10.1525 12.3854 9.66809C12.1849 9.18371 11.8452 8.76971 11.4094 8.47844C10.9736 8.18716 10.4613 8.03169 9.93721 8.03169ZM9.93721 1.40464C10.1993 1.40464 10.4554 1.48238 10.6733 1.62801C10.8912 1.77365 11.061 1.98065 11.1613 2.22284C11.2616 2.46503 11.2878 2.73152 11.2367 2.98863C11.1856 3.24573 11.0594 3.4819 10.8741 3.66726C10.6888 3.85262 10.4527 3.97885 10.1957 4.02999C9.93868 4.08114 9.67227 4.05489 9.43017 3.95457C9.18806 3.85425 8.98113 3.68437 8.83554 3.46641C8.68996 3.24845 8.61225 2.99219 8.61225 2.73005C8.61225 2.37853 8.75184 2.04141 9.00032 1.79284C9.2488 1.54428 9.58581 1.40464 9.93721 1.40464ZM2.64993 8.03169C2.38787 8.03169 2.13171 7.95396 1.91382 7.80832C1.69593 7.66268 1.5261 7.45568 1.42582 7.21349C1.32554 6.97131 1.2993 6.70481 1.35042 6.44771C1.40155 6.1906 1.52774 5.95444 1.71304 5.76908C1.89834 5.58371 2.13442 5.45748 2.39144 5.40634C2.64846 5.3552 2.91486 5.38144 3.15697 5.48176C3.39907 5.58208 3.606 5.75196 3.75159 5.96992C3.89718 6.18789 3.97489 6.44414 3.97489 6.70628C3.97489 7.0578 3.83529 7.39493 3.58681 7.64349C3.33834 7.89205 3.00133 8.03169 2.64993 8.03169ZM9.93721 12.0079C9.67516 12.0079 9.41899 11.9302 9.2011 11.7845C8.98321 11.6389 8.81339 11.4319 8.7131 11.1897C8.61282 10.9475 8.58658 10.681 8.63771 10.4239C8.68883 10.1668 8.81502 9.93067 9.00032 9.74531C9.18562 9.55994 9.4217 9.43371 9.67872 9.38257C9.93574 9.33143 10.2021 9.35767 10.4442 9.45799C10.6864 9.55831 10.8933 9.72819 11.0389 9.94615C11.1845 10.1641 11.2622 10.4204 11.2622 10.6825C11.2622 11.034 11.1226 11.3712 10.8741 11.6197C10.6256 11.8683 10.2886 12.0079 9.93721 12.0079Z'
      fill='white'
    />
  </svg>
);

interface VoucherUpsellModalProps {
  onClose: () => void;
}

export const VoucherUpsellModal = ({ onClose }: VoucherUpsellModalProps) => {
  const { t } = useIntl();
  const isModalOpen = !!useQueryParam(VOUCHER_UPSELL_PARAM);
  const { isCopied, copy, share } = useShareVoucher();
  const { generateVoucher, voucherCode, isGenerating, isGenerateError } = useGenerateVoucher();

  useBodyScroll(isModalOpen);

  useEffect(() => {
    if (isModalOpen && !voucherCode && !isGenerating && !isGenerateError) {
      generateVoucher('womens_day');
    }
  }, [isModalOpen, voucherCode, generateVoucher, isGenerating, isGenerateError]);

  useEffect(() => {
    if (isModalOpen) {
      report(TrackingEvents.WOMENS_DAY.PAGE_VISITED, true);
    }
  }, [isModalOpen]);

  const copyButtonLabel = isCopied ? t({ id: 'common.copied' }) : t({ id: 'common.copy' });
  const copyButtonClassName = `${styles.copyButton} ${isCopied ? styles.copyButtonCopied : ''} display-flex grid-align-center grid-justify-center flex-shrink-0  border-none cursor-pointer`;
  const iconWrapperClassName = `${styles.iconWrapper} display-flex grid-align-center grid-justify-center absolute-reference`;
  const copyIconClassName = `${styles.icon} ${!isCopied ? styles.iconVisible : styles.iconHidden} display-flex grid-align-center grid-justify-center absolute-content`;
  const checkIconClassName = `${styles.icon} ${isCopied ? styles.iconVisible : styles.iconHidden} display-flex grid-align-center grid-justify-center absolute-content`;

  const handleCopyCode = useCallback(() => {
    if (voucherCode) {
      copy(voucherCode);
      report(TrackingEvents.WOMENS_DAY.VOUCHER_GET, true);
    }
  }, [voucherCode, copy]);

  const handleShare = useCallback(async () => {
    if (voucherCode) {
      report(TrackingEvents.WOMENS_DAY.VOUCHER_SHARE, true);
      await share(
        {
          title: t({ id: 'voucherUpsellBanner.modal.shareTitle' }),
          text: t(
            { id: 'voucherUpsellBanner.modal.shareText' },
            { code: voucherCode, url: `${campaigns.WOMENS_DAY_VOUCHER_CHECKOUT_URL}${voucherCode}` },
          ),
        },
        voucherCode,
      );
    }
  }, [voucherCode, t, share]);

  const handleClose = useCallback(() => {
    report(TrackingEvents.WOMENS_DAY.VOUCHER_CLOSE, true);
    onClose();
  }, [onClose]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal
      visible
      title={t({ id: 'voucherUpsellBanner.modal.title' })}
      onClose={handleClose}
      zIndex={2600}
      primaryFooterBtn={
        <Modal.FooterPrimaryBtn
          label={t({ id: 'voucherUpsellBanner.modal.shareButton' })}
          onClick={handleShare}
          leftIcon={<ShareIcon />}
        />
      }
    >
      <div className='display-flex grid-align-start gap-s'>
        <IconS24ShieldCheckmark24 />
        <div className='display-flex grid-justify-space-between grid-align-start one-whole'>
          <div>{t({ id: 'voucherUpsellBanner.modal.description' })}</div>
          <div>
            <UnitedSVG />
          </div>
        </div>
      </div>
      <div className={styles.voucherSection}>
        <div className='font-body-medium-regular color-grey-dark margin-bottom-xs'>
          {t({ id: 'voucherUpsellBanner.modal.codeLabel' })}
        </div>
        <div className='display-flex grid-align-center gap-s'>
          <div
            className={`font-heading-small-bold margin-none ${styles.code} ${isGenerating || !voucherCode ? 'loading' : ''}`}
          >
            {voucherCode || '\u00A0'}
          </div>
          <div className={`flex-shrink-0 absolute-reference ${styles.copyButtonWrapper}`}>
            <button
              className={copyButtonClassName}
              onClick={handleCopyCode}
              aria-label={copyButtonLabel}
              title={copyButtonLabel}
            >
              <div className={iconWrapperClassName}>
                <div className={copyIconClassName}>
                  <CopyIcon />
                </div>
                <div className={checkIconClassName}>
                  <CheckIcon />
                </div>
              </div>
            </button>
            {isCopied && (
              <span className={`${styles.copiedBadge} absolute-content font-body-small-regular white-space-nowrap`}>
                {copyButtonLabel}
              </span>
            )}
          </div>
        </div>
      </div>
      <div
        className={`notification notification--neutral notification--flat margin-bottom-l height-auto absolute-reference ${styles.notificationBox}`}
      >
        <div className='notification__icon'>
          <IconS24InfoCircle24 />
        </div>
        <div className='notification__content'>
          <div className='font-body-small-regular'>{t({ id: 'voucherUpsellBanner.modal.validityInfo' })}</div>
        </div>
      </div>
    </Modal>
  );
};
